/**
 * useMarketData — React hook for live LoopNet market data
 *
 * Usage:
 *   const { marketStats, loading, error, refresh } = useMarketData('Chicago, IL')
 *   // or pass a 2-letter state code:
 *   const { marketStats } = useMarketData('TX')
 *
 * `marketStats`  — LoopNet market stats: avg cap rate, total listings, avg price, avg price/sqft
 * `loading`      — true while the first fetch is in-flight
 * `error`        — error message string or null
 * `refresh`      — call to force a re-fetch (ignores cache)
 *
 * Backward-compat aliases (always null/empty now that FRED + RentCast are gone):
 *   `macro`       — null
 *   `stateMarket` — null
 *   `stateStats`  — same as marketStats (kept so existing consumers don't crash)
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchLocationMarketStats } from '../services/marketDataService'

const LOOPNET_MARKET_STATS_ENABLED = import.meta.env.VITE_LOOPNET_MARKET_STATS_ENABLED === 'true'

const STATE_DEFAULT_CITY = {
  TX: 'Austin, TX',
  FL: 'Miami, FL',
  AZ: 'Phoenix, AZ',
  CA: 'Los Angeles, CA',
  NY: 'New York, NY',
  IL: 'Chicago, IL',
  GA: 'Atlanta, GA',
  CO: 'Denver, CO',
  NC: 'Charlotte, NC',
  TN: 'Nashville, TN',
  WA: 'Seattle, WA',
  MA: 'Boston, MA',
  PA: 'Philadelphia, PA',
  OH: 'Columbus, OH',
}

function resolveLocation(input) {
  if (!input) return 'Chicago, IL'
  // Treat anything that looks like a 2-letter state code as such
  if (typeof input === 'string' && /^[A-Z]{2}$/.test(input.trim())) {
    return STATE_DEFAULT_CITY[input.trim()] || input.trim()
  }
  return input
}

export default function useMarketData(location = 'Chicago, IL') {
  const [marketStats, setMarketStats] = useState(null)
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [lastFetchedAt, setLastFetchedAt] = useState(null)
  const fetchCount = useRef(0)

  const resolved = resolveLocation(location)

  const load = useCallback(async () => {
    if (!LOOPNET_MARKET_STATS_ENABLED) {
      setMarketStats(null)
      setLoading(false)
      setError(null)
      return
    }

    const myFetch = ++fetchCount.current
    setLoading(true)
    setError(null)

    try {
      const stats = await fetchLocationMarketStats(resolved)
      if (myFetch !== fetchCount.current) return
      setMarketStats(stats)
      setLastFetchedAt(new Date())
    } catch (err) {
      if (myFetch !== fetchCount.current) return
      setError(err.message || 'Failed to fetch LoopNet market data')
    } finally {
      if (myFetch === fetchCount.current) setLoading(false)
    }
  }, [resolved])

  useEffect(() => {
    load()
  }, [load])

  // Backward-compat aliases so existing consumers don't break
  return {
    marketStats,
    macro: null,
    stateMarket: null,
    stateStats: marketStats,
    loading,
    error,
    lastFetchedAt,
    refresh: load,
  }
}
